import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Button } from '../../../shared/button/Button';
import styles from './Theoretical.module.scss';
import { calculateNettingEngineStarted, NettingEngineSurvey, setShowSummary, getShowSummary, getIsChangingQuestion, getCurrentNettingEngineData } from '../store';

interface TheoreticalSurveyButtonsProps {
    surveyQuestionIndex: number;
    nettingEngineSurvey: NettingEngineSurvey[];
    setQuestionIndex: (index: number) => void;
    agreementTypeSelected?: boolean;
}

export const TheoreticalSurveyButtons: React.FC<TheoreticalSurveyButtonsProps> = ({
    surveyQuestionIndex,
    nettingEngineSurvey,
    setQuestionIndex,
    agreementTypeSelected = true,
}) => {

    const dispatch = useAppDispatch();

    const changingQuestion = useAppSelector(getIsChangingQuestion);
    const showSummary = useAppSelector(getShowSummary);
    const theoreticalAnswers = useAppSelector(getCurrentNettingEngineData);

    const calculate = useCallback(() => dispatch(calculateNettingEngineStarted()), [dispatch]);

    const previousQuestion = useCallback(() => {
        if (showSummary) {
            dispatch(setShowSummary(false));
        } else {
            setQuestionIndex(surveyQuestionIndex - 1);
        }
    }, [surveyQuestionIndex, setQuestionIndex, dispatch, showSummary]);

    const nextQuestion = useCallback(() => surveyQuestionIndex === nettingEngineSurvey.length - 1 ? dispatch(setShowSummary(true)) : setQuestionIndex(surveyQuestionIndex + 1), [surveyQuestionIndex, nettingEngineSurvey, dispatch, setQuestionIndex]);

    const canPrevious = useMemo(() => surveyQuestionIndex !== 0, [surveyQuestionIndex]);

    const canNext = useMemo(() => {
        if (
            isNull(theoreticalAnswers) ||
            surveyQuestionIndex === nettingEngineSurvey.length ||
            (surveyQuestionIndex === 1 && isNull(theoreticalAnswers.counterparty.jurisdiction))
        ) {
            return false;
        }
        return !changingQuestion && !isNull(nettingEngineSurvey[surveyQuestionIndex].value);
    }, [nettingEngineSurvey, surveyQuestionIndex, changingQuestion, theoreticalAnswers]);

    return (
        <div className={styles.surveyButtonWrapper}>
            <div className={styles.surveyButtonPrevious}>{surveyQuestionIndex !== 0 && <Button label='Previous' onClick={previousQuestion} disabled={!canPrevious} />}</div>
            {!showSummary && agreementTypeSelected && <div className={styles.surveyQuestionCounter}>{surveyQuestionIndex + 1} of {nettingEngineSurvey.length}</div>}
            <div className={styles.surveyButtonNext}>
                {!showSummary && canNext && !changingQuestion && <Button label='Next' onClick={nextQuestion} disabled={!canNext} />}
                {showSummary && <Button label='Calculate' onClick={calculate} />}
            </div>
        </div>
    );
};
